import { CheckCircle, Close, Error, Info, Warning } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Slide,
  Snackbar,
  SnackbarProps,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import React from "react";
import {
  hideMainErrorPopup,
  MainErrorInfo,
} from "../../redux/slice/commonSlice";
import { useAppDispatch } from "../../redux/slice/redux-hooks";

interface CustomSnackbarOrigin {
  vertical: "top" | "bottom";
  horizontal: "left" | "right" | "center";
}

interface CustomAlertProps {
  open: boolean;
  onClose?: () => void;
  severity: "error" | "success" | "warning" | "info";
  mainErrorObj?: MainErrorInfo | null;
  messageStream?: string;
  messageList?: string[];
  content?: React.ReactNode;
  variant?: "filled" | "outlined";
  snackbarProps?: SnackbarProps;
  alertTitle?: string;
  anchorOrigin?: CustomSnackbarOrigin;
  sx?: SxProps<Theme>;
}

const T = {
  bgPanel: "#18242f",
  bgInput: "#26384b",
  border: "rgba(255,255,255,0.10)",
  borderFocus: "rgba(0,210,180,0.45)",
  textPrimary: "#e8f0f8",
  textSecondary: "#7a92a8",
  accent: "#00d2b4",
  accentB: "#00b8f5",
};

const getPaperSx = (fullScreen: boolean): SxProps<Theme> => ({
  position: "relative",
  bgcolor: T.bgPanel,
  border: `1px solid ${T.border}`,
  borderRadius: fullScreen ? 0 : "12px",
  overflow: "hidden",
  boxShadow:
    "0 8px 32px rgba(0,0,0,0.50), 0 1px 0 rgba(255,255,255,0.04) inset",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "2px",
    background: `linear-gradient(90deg, transparent, ${T.accent} 35%, ${T.accentB} 65%, transparent)`,
    opacity: 0.75,
  },
});

const headerSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  px: 2.5,
  pt: 2.5,
  pb: 1.8,
  borderBottom: `1px solid ${T.border}`,
};

const bodySx: SxProps<Theme> = {
  px: 2.5,
  py: 2,
  display: "flex",
  flexDirection: "column",
  gap: 1,
};

const footerSx: SxProps<Theme> = {
  px: 2.5,
  pt: 1.5,
  pb: 2.2,
  borderTop: `1px solid ${T.border}`,
};

const closeSx: SxProps<Theme> = {
  width: 28,
  height: 28,
  ml: "auto",
  flexShrink: 0,
  border: `1px solid ${T.border}`,
  bgcolor: T.bgInput,
  borderRadius: "7px",
  color: T.textSecondary,
  transition: "all 0.13s",
  "&:hover": {
    borderColor: T.borderFocus,
    color: T.textPrimary,
    bgcolor: T.bgInput,
  },
};

const cancelBtnSx: SxProps<Theme> = {
  px: 2.2,
  py: 0.75,
  borderRadius: "8px",
  fontSize: "0.82rem",
  fontWeight: 600,
  textTransform: "none",
  border: `1px solid ${T.border}`,
  bgcolor: T.bgInput,
  color: T.textSecondary,
  "&:hover": {
    bgcolor: T.bgInput,
    borderColor: "rgba(255,255,255,0.18)",
    color: T.textPrimary,
  },
};

const primaryBtnSx: SxProps<Theme> = {
  px: 2.6,
  py: 0.75,
  minWidth: 96,
  borderRadius: "8px",
  fontSize: "0.82rem",
  fontWeight: 600,
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  color: "#e8f0f8",
  bgcolor: "#3a9187",
  boxShadow: "none",
  "&:hover": {
    color: "#ffffff",
    bgcolor: "#42a397",
    boxShadow: "none",
  },
};

const iconBoxSx = (hex: string, glow: string): SxProps<Theme> => ({
  width: 36,
  height: 36,
  borderRadius: "10px",
  background: `${hex}12`,
  border: `1px solid ${hex}30`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  boxShadow: `0 0 12px ${glow}`,
});

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const CustomErrorAlert: React.FC<CustomAlertProps> = ({
  open,
  onClose,
  severity,
  mainErrorObj,
  messageStream,
  messageList,
  content,
  snackbarProps,
  alertTitle,
  anchorOrigin,
  sx,
}) => {
  const dispatch = useAppDispatch();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  if (mainErrorObj) {
    alertTitle = mainErrorObj.title;
    messageStream = mainErrorObj.errorMessageStream;
    messageList = mainErrorObj.errorMessageList;
  }

  const alertTitleValue = alertTitle || "Error";
  const replaceNbsp = (str: string) => str.replace(/&nbsp;/g, "");
  const cleanLine = (str: string) =>
    replaceNbsp(str)
      .replace(/^\*+\s*/, "")
      .trim();
  const onClosePopup = () => dispatch(hideMainErrorPopup());
  const handleClose = onClose ?? mainErrorObj?.onCloseExt ?? onClosePopup;

  const severityConfig = {
    error: {
      icon: <Error sx={{ color: "#f87171", fontSize: 20 }} />,
      iconHex: "#f87171",
      iconGlow: "rgba(248,113,113,0.18)",
      iconBg: "linear-gradient(135deg, #b91c1c, #7f1d1d)",
      accentColor: "#f87171",
      tagBg: "rgba(248,113,113,0.07)",
      tagBorder: "rgba(248,113,113,0.18)",
      tagHover: "rgba(248,113,113,0.12)",
      subtitleText: "Review the issues below before continuing.",
    },
    warning: {
      icon: <Warning sx={{ color: "#fbbf24", fontSize: 20 }} />,
      iconHex: "#fbbf24",
      iconGlow: "rgba(251,191,36,0.18)",
      iconBg: "linear-gradient(135deg, #d97706, #92400e)",
      accentColor: "#fbbf24",
      tagBg: "rgba(251,191,36,0.07)",
      tagBorder: "rgba(251,191,36,0.18)",
      tagHover: "rgba(251,191,36,0.12)",
      subtitleText: "Please review the warnings below.",
    },
    success: {
      icon: <CheckCircle sx={{ color: T.accent, fontSize: 20 }} />,
      iconHex: T.accent,
      iconGlow: "rgba(0,210,180,0.18)",
      iconBg: `linear-gradient(135deg, ${T.accent}, ${T.accentB})`,
      accentColor: T.accent,
      tagBg: "rgba(0,210,180,0.07)",
      tagBorder: "rgba(0,210,180,0.18)",
      tagHover: "rgba(0,210,180,0.12)",
      subtitleText: "Operation completed successfully.",
    },
    info: {
      icon: <Info sx={{ color: T.accentB, fontSize: 20 }} />,
      iconHex: T.accentB,
      iconGlow: "rgba(0,184,245,0.18)",
      iconBg: `linear-gradient(135deg, ${T.accentB}, #0070cc)`,
      accentColor: T.accentB,
      tagBg: "rgba(0,184,245,0.07)",
      tagBorder: "rgba(0,184,245,0.18)",
      tagHover: "rgba(0,184,245,0.12)",
      subtitleText: "Here is some information for you.",
    },
  };

  const cfg = severityConfig[severity] ?? severityConfig.error;

  const streamLines = messageStream
    ? replaceNbsp(messageStream)
        .split("\n")
        .filter((l) => l.trim() !== "")
    : [];
  const errorLines = messageList
    ? messageList.map(cleanLine).filter((l) => l !== "")
    : streamLines.map(cleanLine).filter((l) => l !== "");
  const subtitleContent =
    !messageList && !messageStream && content ? content : null;

  const dialogBody = (
    <>
      <Box sx={headerSx}>
        <Box sx={iconBoxSx(cfg.iconHex, cfg.iconGlow)}>{cfg.icon}</Box>

        <Box>
          <Typography
            fontWeight={700}
            sx={{ fontSize: "1.1rem", color: T.textPrimary, lineHeight: 1.3 }}
          >
            {alertTitleValue}
          </Typography>
          <Typography
            sx={{ fontSize: "0.8rem", color: T.textSecondary, mt: 0.15 }}
          >
            {subtitleContent ? "" : cfg.subtitleText}
          </Typography>
        </Box>

        <IconButton onClick={handleClose} size="small" sx={closeSx}>
          <Close sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      <Box sx={bodySx}>
        {subtitleContent && (
          <Typography
            variant="body2"
            sx={{ color: T.textSecondary, whiteSpace: "pre-line" }}
          >
            {subtitleContent}
          </Typography>
        )}

        {errorLines.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {errorLines.map((line, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.4,
                  background: cfg.tagBg,
                  border: `1px solid ${cfg.tagBorder}`,
                  borderRadius: "10px",
                  px: 1.8,
                  py: 1.1,
                  transition: "background 0.18s, border-color 0.18s",
                  "&:hover": {
                    background: cfg.tagHover,
                    borderColor: `${cfg.accentColor}40`,
                  },
                }}
              >
                <Box
                  sx={{
                    minWidth: 22,
                    height: 22,
                    borderRadius: "6px",
                    background: cfg.iconBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    mt: 0.15,
                    boxShadow: `0 2px 6px ${cfg.iconGlow}`,
                  }}
                >
                  <Typography
                    sx={{
                      color: "#fff",
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    {index + 1}
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: cfg.accentColor,
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    lineHeight: 1.55,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {line}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Box sx={footerSx}>
        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
          {mainErrorObj?.buttonName && (
            <Button
              variant="outlined"
              onClick={mainErrorObj?.onOkExt}
              sx={cancelBtnSx}
            >
              {mainErrorObj.buttonName}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={mainErrorObj?.onOkExt ?? handleClose}
            sx={primaryBtnSx}
          >
            Got&nbsp;it
          </Button>
        </Box>
      </Box>
    </>
  );

  if (mainErrorObj?.popupType === "ServerErrorAlert") {
    return (
      <Snackbar
        open={open}
        anchorOrigin={anchorOrigin || { vertical: "top", horizontal: "center" }}
        onClick={(e) => e.stopPropagation()}
        {...snackbarProps}
      >
        <Box
          sx={{
            ...getPaperSx(false),
            maxWidth: 460,
            minWidth: 300,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={headerSx}>
            <Box sx={iconBoxSx(cfg.iconHex, cfg.iconGlow)}>{cfg.icon}</Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                fontWeight={700}
                sx={{ fontSize: "1rem", color: T.textPrimary, lineHeight: 1.3 }}
              >
                {alertTitle || alertTitleValue}
              </Typography>
              <Typography
                sx={{ fontSize: "0.78rem", color: T.textSecondary, mt: 0.1 }}
              >
                {cfg.subtitleText}
              </Typography>
            </Box>
            <IconButton onClick={handleClose} size="small" sx={closeSx}>
              <Close sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Box>

          {(errorLines.length > 0 || subtitleContent) && (
            <Box sx={{ ...bodySx, py: 1.6 }}>
              {subtitleContent && (
                <Typography variant="body2" sx={{ color: T.textSecondary }}>
                  {subtitleContent}
                </Typography>
              )}
              {errorLines.length > 0 && (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.9 }}
                >
                  {errorLines.map((line, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.2,
                        background: cfg.tagBg,
                        border: `1px solid ${cfg.tagBorder}`,
                        borderRadius: "8px",
                        px: 1.5,
                        py: 1,
                        transition: "background 0.18s, border-color 0.18s",
                        "&:hover": {
                          background: cfg.tagHover,
                          borderColor: `${cfg.accentColor}40`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: 20,
                          height: 20,
                          borderRadius: "5px",
                          background: cfg.iconBg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          mt: 0.1,
                          boxShadow: `0 2px 6px ${cfg.iconGlow}`,
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#fff",
                            fontSize: "0.63rem",
                            fontWeight: 700,
                          }}
                        >
                          {index + 1}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: cfg.accentColor,
                          fontWeight: 500,
                          fontSize: "0.84rem",
                        }}
                      >
                        {line}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          <Box sx={footerSx}>
            <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={mainErrorObj?.onOkExt ?? handleClose}
                sx={primaryBtnSx}
              >
                {mainErrorObj?.buttonName || "Got it"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Snackbar>
    );
  }

  if (mainErrorObj?.popupType === "Dialog") {
    return (
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: getPaperSx(isMobile) }}
        sx={{
          "& .MuiBackdrop-root": {
            bgcolor: "rgba(5,10,18,0.72)",
            backdropFilter: "blur(5px)",
          },
          ...sx,
        }}
      >
        {dialogBody}
      </Dialog>
    );
  }

  return (
    <Snackbar
      open={open}
      anchorOrigin={anchorOrigin || { vertical: "top", horizontal: "center" }}
      onClick={(e) => e.stopPropagation()}
      {...snackbarProps}
    >
      <Box
        sx={{
          ...getPaperSx(false),
          maxWidth: 480,
          minWidth: 300,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {dialogBody}
      </Box>
    </Snackbar>
  );
};

export default CustomErrorAlert;
